import { useState, useEffect } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Info,
  AlertTriangle,
  CheckCircle2,
  Plus,
  Trash2,
  ArrowRight,
  ArrowLeft,
  Loader2,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

const WEBHOOK_URL =
  "https://services.leadconnectorhq.com/hooks/nsp8u9BgTJB40BULOWfw/webhook-trigger/4f07a1a7-3a4b-4cb2-a6f9-99e56fa578df";
const GOOGLE_SHEET_CSV_URL =
  "https://docs.google.com/spreadsheets/d/e/2PACX-1vSgKLikY27fgS4at-CQptvVowicUFr9IoDbuTAb5P_bzSwPCSOBBLfn5vn5RfBspuVz8Fvswnl6SopM/pub?output=csv";

const BASE_PRICE = 749;
const INCLUDED_TOWNS = 7;
const ADDITIONAL_TOWN_PRICE = 70;
const SETUP_FEE = 900;
const VAT_RATE = 0.2;

type Town = { name: string; code: string };

function extractDomain(url: string) {
  if (!url) return "";
  return url
    .replace(new RegExp("^https?:\\\\/\\\\/"), "")
    .replace(new RegExp("^www\\\\."), "")
    .replace(new RegExp("\\\\/.*$"), "")
    .trim();
}

function parseCSVLine(line: string) {
  const result = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === "," && !inQuotes) {
      result.push(current.replace(/^"|"$/g, "").trim());
      current = "";
    } else {
      current += char;
    }
  }
  result.push(current.replace(/^"|"$/g, "").trim());
  return result;
}

const Signup = () => {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [existingTowns, setExistingTowns] = useState<
    { name: string; areaCode: string }[]
  >([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState({
    name: "",
    businessName: "",
    streetAddress: "",
    town: "",
    postcode: "",
    businessPhone: "",
    personalPhone: "",
    businessEmail: "",
    websiteAddress: "",
    businessType: "",
    companyRegistrationNumber: "",
    paymentMethods: [] as string[],
    goCardlessLink: "",
    reviewLink: "",
    minPrice: "",
    yearlyValue: "",
    goLivePreference: "",
    goLiveDate: "",
    towns: [{ name: "", code: "" }] as Town[],
    includeVat: true,
  });

  useEffect(() => {
    const fetchTowns = async () => {
      const corsProxies = [
        "",
        "https://corsproxy.io/?",
        "https://api.allorigins.win/raw?url=",
      ];

      for (const proxy of corsProxies) {
        try {
          const fetchUrl = proxy
            ? proxy + encodeURIComponent(GOOGLE_SHEET_CSV_URL)
            : GOOGLE_SHEET_CSV_URL;
          const response = await fetch(fetchUrl);
          if (!response.ok) continue;

          const text = await response.text();
          const lines = text.split(/\\r?\\n/);
          const parsed = [];
          for (let i = 1; i < lines.length; i++) {
            const line = lines[i].trim();
            if (!line) continue;
            const parts = parseCSVLine(line);
            const townName = (parts[0] || "").trim().toLowerCase();
            const areaCode = (parts[1] || "").trim();
            if (townName && townName !== "town") {
              parsed.push({ name: townName, areaCode });
            }
          }
          setExistingTowns(parsed);
          break; // Success, stop trying proxies
        } catch (error) {
          console.error(
            `Failed to fetch towns with proxy ${proxy || "direct"}`,
            error,
          );
        }
      }
    };
    fetchTowns();
  }, []);

  const checkTownWarning = (name: string, code: string) => {
    if (!name || !code) return false;
    const townName = name.trim().toLowerCase();
    const areaCode = code.trim();

    return existingTowns.some((existing) => {
      const townMatch = existing.name === townName;
      const codeMatch =
        existing.areaCode === areaCode ||
        existing.areaCode === areaCode.replace(/^0+/, "") ||
        "0" + existing.areaCode === areaCode;
      return townMatch && codeMatch;
    });
  };

  const validateStep1 = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.businessName.trim())
      newErrors.businessName = "Business Name is required";
    if (!formData.streetAddress.trim())
      newErrors.streetAddress = "Street Address is required";
    if (!formData.town.trim()) newErrors.town = "Town is required";
    if (!formData.postcode.trim()) newErrors.postcode = "Postcode is required";
    if (!formData.businessPhone.trim())
      newErrors.businessPhone = "Business Phone is required";
    if (!formData.personalPhone.trim())
      newErrors.personalPhone = "Personal Phone is required";
    if (!formData.businessEmail.trim()) {
      newErrors.businessEmail = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.businessEmail)) {
      newErrors.businessEmail = "Please enter a valid email";
    }
    if (!formData.websiteAddress.trim())
      newErrors.websiteAddress = "Website Address is required";

    if (!formData.businessType)
      newErrors.businessType = "Please select a business type";
    if (
      formData.businessType === "Limited Company" &&
      !formData.companyRegistrationNumber.trim()
    ) {
      newErrors.companyRegistrationNumber = "Registration number is required";
    }

    if (formData.paymentMethods.length === 0)
      newErrors.paymentMethods = "Select at least one payment method";
    if (
      formData.paymentMethods.includes("GoCardless") &&
      !formData.goCardlessLink.trim()
    ) {
      newErrors.goCardlessLink = "GoCardless signup link is required";
    }

    if (!formData.reviewLink.trim())
      newErrors.reviewLink = "Review link is required";
    if (!formData.minPrice.trim())
      newErrors.minPrice = "Minimum price is required";
    if (!formData.yearlyValue.trim())
      newErrors.yearlyValue = "Yearly value is required";

    if (!formData.goLivePreference)
      newErrors.goLivePreference = "Please select when you'd like to go live";
    if (formData.goLivePreference === "specific" && !formData.goLiveDate) {
      newErrors.goLiveDate = "Please select a date";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      toast({
        title: "Please complete all required fields",
        variant: "destructive",
      });
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    const newErrors: Record<string, string> = {};
    let hasValidTown = false;

    formData.towns.forEach((town, index) => {
      if (town.name.trim() || town.code.trim()) {
        if (!town.name.trim())
          newErrors[`town_${index}_name`] = "Town name required";
        if (!town.code.trim())
          newErrors[`town_${index}_code`] = "Area code required";
        if (town.name.trim() && town.code.trim()) hasValidTown = true;
      }
    });

    if (!hasValidTown) {
      toast({
        title: "Please add at least one town",
        variant: "destructive",
      });
      return false;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const togglePaymentMethod = (method: string) => {
    setFormData((prev) => {
      const methods = [...prev.paymentMethods];
      if (methods.includes(method)) {
        return { ...prev, paymentMethods: methods.filter((m) => m !== method) };
      } else {
        return { ...prev, paymentMethods: [...methods, method] };
      }
    });
  };

  const updateTown = (index: number, field: keyof Town, value: string) => {
    const newTowns = [...formData.towns];
    newTowns[index] = { ...newTowns[index], [field]: value };
    setFormData({ ...formData, towns: newTowns });

    // Clear error for this field
    if (errors[`town_${index}_${field}`]) {
      const newErrors = { ...errors };
      delete newErrors[`town_${index}_${field}`];
      setErrors(newErrors);
    }
  };

  const addTown = () => {
    setFormData({
      ...formData,
      towns: [...formData.towns, { name: "", code: "" }],
    });
  };

  const removeTown = (index: number) => {
    if (formData.towns.length <= 1) return;
    const newTowns = [...formData.towns];
    newTowns.splice(index, 1);
    setFormData({ ...formData, towns: newTowns });
  };

  const handleSubmit = async () => {
    if (!validateStep2()) return;

    setIsSubmitting(true);

    const validTowns = formData.towns.filter(
      (t) => t.name.trim() && t.code.trim(),
    );
    // Sort towns alphabetically
    validTowns.sort((a, b) =>
      a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
    );

    const validTownsCount = validTowns.length;
    const extraTowns = Math.max(0, validTownsCount - INCLUDED_TOWNS);
    const monthlyFee = BASE_PRICE + extraTowns * ADDITIONAL_TOWN_PRICE;
    const monthlyVat = formData.includeVat ? monthlyFee * VAT_RATE : 0;
    const setupVat = formData.includeVat ? SETUP_FEE * VAT_RATE : 0;

    const payload = {
      name: formData.name,
      business_name: formData.businessName,
      street_address: formData.streetAddress,
      town: formData.town,
      postcode: formData.postcode,
      business_phone: formData.businessPhone,
      personal_phone: formData.personalPhone,
      business_email: formData.businessEmail,
      website_address: formData.websiteAddress,
      website_suffix: extractDomain(formData.websiteAddress),
      business_type: formData.businessType,
      company_registration_number:
        formData.businessType === "Limited Company"
          ? formData.companyRegistrationNumber
          : "",
      payment_methods: formData.paymentMethods.join(", "),
      gocardless_link: formData.paymentMethods.includes("GoCardless")
        ? formData.goCardlessLink
        : "",
      review_link: formData.reviewLink,
      min_window_cleaning_price: formData.minPrice,
      yearly_round_value: formData.yearlyValue,
      go_live_preference:
        formData.goLivePreference === "ASAP" ? "ASAP" : formData.goLiveDate,
      towns: validTowns.map((t) => ({ town_name: t.name, area_code: t.code })),
      town_names: validTowns.map((t) => t.name),
      area_codes: validTowns.map((t) => t.code),
      town_count: validTowns.length,
      monthly_fee_ex_vat: monthlyFee,
      monthly_vat: monthlyVat,
      monthly_total: monthlyFee + monthlyVat,
      setup_fee_ex_vat: SETUP_FEE,
      setup_vat: setupVat,
      setup_total: SETUP_FEE + setupVat,
      vat_applicable: formData.includeVat,
    };

    try {
      await fetch(WEBHOOK_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const trackingPayload = {
        type: "external_form_submission",
        timestamp: Date.now(),
        formId: "Signup Form",
        formData: {
          first_name: formData.name,
          email: formData.businessEmail,
          phone: formData.personalPhone,
          organization: formData.businessName,
          address: formData.streetAddress,
          city: formData.town,
          postal_code: formData.postcode,
          website: formData.websiteAddress,
        },
        formLabels: {
          first_name: "Name",
          email: "Business Email",
          phone: "Personal Phone",
          organization: "Business Name",
          address: "Street Address",
          city: "Town",
          postal_code: "Postcode",
          website: "Website Address",
        },
        url: window.location.href,
        title: document.title,
        path: window.location.pathname,
        userAgent: navigator.userAgent,
        trackingId: "tk_4228c869be4b43a6af25f3931923e54d",
        locationId: "nsp8u9BgTJB40BULOWfw",
        sessionId: crypto.randomUUID(),
        properties: {
          deviceType: /Mobile|Android|iPhone/i.test(navigator.userAgent)
            ? "mobile"
            : "desktop",
        },
      };

      fetch("https://backend.leadconnectorhq.com/external-tracking/events", {
        method: "POST",
        headers: { "Content-Type": "application/json", version: "2021-07-28" },
        body: JSON.stringify(trackingPayload),
      }).catch(() => {});

      setIsSuccess(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error("Submission error:", error);
      toast({
        title: "Submission Failed",
        description:
          "There was an error submitting your form. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const validTownsCount = formData.towns.filter((t) => t.name.trim()).length;
  const extraTowns = Math.max(0, validTownsCount - INCLUDED_TOWNS);
  const monthlyFee = BASE_PRICE + extraTowns * ADDITIONAL_TOWN_PRICE;
  const monthlyVat = formData.includeVat ? monthlyFee * VAT_RATE : 0;
  const setupVat = formData.includeVat ? SETUP_FEE * VAT_RATE : 0;
  const monthlyTotal = monthlyFee + monthlyVat;
  const setupTotal = SETUP_FEE + setupVat;

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-slate-50 font-sans text-foreground flex flex-col">
        <SEOHead
          title="Sign Up | Clean Marketing - Window Cleaning Marketing"
          description="Onboard your window cleaning business with Clean Marketing. Set up your target towns, pricing, and payment details to start generating bookings."
          canonical="/signup"
        />
        <Header />
        <main className="flex-grow container mx-auto px-4 py-20 max-w-2xl flex items-center justify-center text-center">
          <div>
            <div className="w-24 h-24 bg-primary rounded-full flex items-center justify-center mx-auto mb-8 shadow-[0_0_40px_rgba(254,82,64,0.3)]">
              <CheckCircle2 className="w-12 h-12 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-secondary mb-4">
              Thank You!
            </h1>
            <p className="text-xl text-slate-600 mb-10">
              Your onboarding form has been submitted successfully.
            </p>

            <Card className="text-left border-slate-200 shadow-xl bg-white/90 backdrop-blur">
              <CardHeader>
                <CardTitle className="flex items-center text-xl text-secondary gap-3">
                  <Info className="w-6 h-6 text-primary" />
                  What Happens Next?
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-slate-600">
                <p>
                  You will shortly receive an email with further instructions.
                  Please reply to this email with the required attachments and
                  images to complete your setup.
                </p>
                <p>
                  Once your application has been accepted, you will receive an
                  invoice for the setup fee. More details will be included in
                  the email.
                </p>
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <p className="font-medium text-secondary">
                    Haven't received the email?
                  </p>
                  <p>
                    Please check your junk or spam folder. If you still can't
                    find it, get in touch with us on{" "}
                    <strong className="text-secondary">01225 234456</strong>.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-foreground flex flex-col">
      <Header />

      <main className="flex-grow container mx-auto px-4 py-16 max-w-3xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-secondary mb-4">
            {step === 1 ? "Welcome Aboard" : "Target Areas"}
          </h1>
          <p className="text-lg text-secondary/60">
            {step === 1
              ? "Let's get your business set up"
              : "Select the towns you'd like to target"}
          </p>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-4 mb-12">
          <div className="flex items-center gap-2">
            <div
              className={cn(
                "w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-colors",
                step === 1
                  ? "bg-secondary text-white"
                  : "bg-secondary text-white",
              )}
            >
              {step === 2 ? <CheckCircle2 className="w-5 h-5" /> : "1"}
            </div>
            <span
              className={cn(
                "text-sm font-medium hidden sm:block",
                step === 1 ? "text-secondary" : "text-secondary/50",
              )}
            >
              Business Details
            </span>
          </div>
          <div
            className={cn(
              "w-16 h-1 rounded-full transition-colors",
              step === 2 ? "bg-secondary" : "bg-secondary/20",
            )}
          />
          <div className="flex items-center gap-2">
            <div
              className={cn(
                "w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-colors",
                step === 2
                  ? "bg-secondary text-white"
                  : "bg-secondary/15 text-secondary",
              )}
            >
              2
            </div>
            <span
              className={cn(
                "text-sm font-medium hidden sm:block",
                step === 2 ? "text-secondary" : "text-secondary/50",
              )}
            >
              Target Areas
            </span>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
          {step === 1 && (
            <div className="p-6 md:p-10 space-y-10">
              {/* Section 1: Your Details */}
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-secondary flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-secondary text-white flex items-center justify-center text-sm">
                    1
                  </span>
                  Your Details
                </h2>

                <div className="space-y-4">
                  <div>
                    <Label
                      htmlFor="name"
                      className="text-secondary mb-1.5 block"
                    >
                      Your Name
                    </Label>
                    <Input
                      id="name"
                      placeholder="John Smith"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      className={cn(errors.name && "border-destructive")}
                    />
                    {errors.name && (
                      <p className="text-sm text-destructive mt-1">
                        {errors.name}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label
                      htmlFor="businessName"
                      className="text-secondary mb-1.5 block"
                    >
                      Business Name
                    </Label>
                    <Input
                      id="businessName"
                      placeholder="Acme Ltd"
                      value={formData.businessName}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          businessName: e.target.value,
                        })
                      }
                      className={cn(
                        errors.businessName && "border-destructive",
                      )}
                    />
                    {errors.businessName && (
                      <p className="text-sm text-destructive mt-1">
                        {errors.businessName}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label
                      htmlFor="streetAddress"
                      className="text-secondary mb-1.5 block"
                    >
                      Street Address
                    </Label>
                    <Input
                      id="streetAddress"
                      placeholder="123 High Street"
                      value={formData.streetAddress}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          streetAddress: e.target.value,
                        })
                      }
                      className={cn(
                        errors.streetAddress && "border-destructive",
                      )}
                    />
                    {errors.streetAddress && (
                      <p className="text-sm text-destructive mt-1">
                        {errors.streetAddress}
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label
                        htmlFor="town"
                        className="text-secondary mb-1.5 block"
                      >
                        Town
                      </Label>
                      <Input
                        id="town"
                        placeholder="London"
                        value={formData.town}
                        onChange={(e) =>
                          setFormData({ ...formData, town: e.target.value })
                        }
                        className={cn(errors.town && "border-destructive")}
                      />
                      {errors.town && (
                        <p className="text-sm text-destructive mt-1">
                          {errors.town}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label
                        htmlFor="postcode"
                        className="text-secondary mb-1.5 block"
                      >
                        Postcode
                      </Label>
                      <Input
                        id="postcode"
                        placeholder="SW1A 1AA"
                        value={formData.postcode}
                        onChange={(e) =>
                          setFormData({ ...formData, postcode: e.target.value })
                        }
                        className={cn(errors.postcode && "border-destructive")}
                      />
                      {errors.postcode && (
                        <p className="text-sm text-destructive mt-1">
                          {errors.postcode}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 2: Contact Info */}
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-secondary flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-secondary text-white flex items-center justify-center text-sm">
                    2
                  </span>
                  Contact Information
                </h2>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label
                        htmlFor="businessPhone"
                        className="text-secondary mb-1.5 block"
                      >
                        Business Phone
                      </Label>
                      <Input
                        id="businessPhone"
                        type="tel"
                        placeholder="020 1234 5678"
                        value={formData.businessPhone}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            businessPhone: e.target.value,
                          })
                        }
                        className={cn(
                          errors.businessPhone && "border-destructive",
                        )}
                      />
                      {errors.businessPhone && (
                        <p className="text-sm text-destructive mt-1">
                          {errors.businessPhone}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label
                        htmlFor="personalPhone"
                        className="text-secondary mb-1.5 block"
                      >
                        Personal Mobile Number
                      </Label>
                      <Input
                        id="personalPhone"
                        type="tel"
                        placeholder="07700 900123"
                        value={formData.personalPhone}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            personalPhone: e.target.value,
                          })
                        }
                        className={cn(
                          errors.personalPhone && "border-destructive",
                        )}
                      />
                      {errors.personalPhone && (
                        <p className="text-sm text-destructive mt-1">
                          {errors.personalPhone}
                        </p>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label
                      htmlFor="businessEmail"
                      className="text-secondary mb-1.5 block"
                    >
                      Business Email
                    </Label>
                    <Input
                      id="businessEmail"
                      type="email"
                      placeholder="hello@example.com"
                      value={formData.businessEmail}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          businessEmail: e.target.value,
                        })
                      }
                      className={cn(
                        errors.businessEmail && "border-destructive",
                      )}
                    />
                    {errors.businessEmail && (
                      <p className="text-sm text-destructive mt-1">
                        {errors.businessEmail}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label
                      htmlFor="websiteAddress"
                      className="text-secondary mb-1.5 block"
                    >
                      Website Address
                    </Label>
                    <Input
                      id="websiteAddress"
                      type="url"
                      placeholder="https://www.example.com"
                      value={formData.websiteAddress}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          websiteAddress: e.target.value,
                        })
                      }
                      className={cn(
                        errors.websiteAddress && "border-destructive",
                      )}
                    />
                    {errors.websiteAddress && (
                      <p className="text-sm text-destructive mt-1">
                        {errors.websiteAddress}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Section 3: Business Type */}
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-secondary flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-secondary text-white flex items-center justify-center text-sm">
                    3
                  </span>
                  Business Type
                </h2>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    {["Limited Company", "Sole-Trader"].map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => {
                          setFormData({ ...formData, businessType: type });
                          if (errors.businessType) {
                            const newErrors = { ...errors };
                            delete newErrors.businessType;
                            setErrors(newErrors);
                          }
                        }}
                        className={cn(
                          "p-4 rounded-xl border text-center font-medium transition-all",
                          formData.businessType === type
                            ? "bg-secondary border-secondary text-white shadow-md"
                            : "bg-white border-slate-200 text-secondary hover:border-secondary/50",
                          errors.businessType && "border-destructive",
                        )}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                  {errors.businessType && (
                    <p className="text-sm text-destructive">
                      {errors.businessType}
                    </p>
                  )}

                  {formData.businessType === "Limited Company" && (
                    <div className="animate-in fade-in slide-in-from-top-2 pt-2">
                      <Label
                        htmlFor="companyReg"
                        className="text-secondary mb-1.5 block"
                      >
                        Company Registration Number
                      </Label>
                      <Input
                        id="companyReg"
                        placeholder="12345678"
                        value={formData.companyRegistrationNumber}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            companyRegistrationNumber: e.target.value,
                          })
                        }
                        className={cn(
                          errors.companyRegistrationNumber &&
                            "border-destructive",
                        )}
                      />
                      {errors.companyRegistrationNumber && (
                        <p className="text-sm text-destructive mt-1">
                          {errors.companyRegistrationNumber}
                        </p>
                      )}
                    </div>
                  )}

                  {formData.businessType === "Sole-Trader" && (
                    <div className="animate-in fade-in slide-in-from-top-2 p-4 bg-secondary/5 border border-secondary/20 rounded-xl flex gap-3 mt-4">
                      <Info className="w-6 h-6 text-secondary shrink-0" />
                      <div>
                        <h4 className="font-medium text-secondary text-sm mb-1">
                          Verification Required
                        </h4>
                        <p className="text-sm text-secondary/70">
                          As a sole trader, you will receive an email requesting
                          photographic ID and proof of address in order to set
                          up your telephone system.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Section 4: Payment Methods */}
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-secondary flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-secondary text-white flex items-center justify-center text-sm">
                    4
                  </span>
                  Payment Methods
                </h2>

                <div className="space-y-4">
                  <p className="text-sm text-secondary/70 mb-2">
                    Select all payment methods you accept
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {[
                      "Cash",
                      "Bank Transfer",
                      "Card",
                      "Cheque",
                      "PayPal",
                      "GoCardless",
                    ].map((method) => (
                      <button
                        key={method}
                        type="button"
                        onClick={() => {
                          togglePaymentMethod(method);
                          if (errors.paymentMethods) {
                            const newErrors = { ...errors };
                            delete newErrors.paymentMethods;
                            setErrors(newErrors);
                          }
                        }}
                        className={cn(
                          "p-3 rounded-xl border text-sm font-medium transition-all flex items-center justify-center gap-2",
                          formData.paymentMethods.includes(method)
                            ? "bg-secondary border-secondary text-white shadow-md"
                            : "bg-white border-slate-200 text-secondary hover:border-secondary/50",
                          errors.paymentMethods && "border-destructive",
                        )}
                      >
                        {formData.paymentMethods.includes(method) && (
                          <CheckCircle2 className="w-4 h-4" />
                        )}
                        {method}
                      </button>
                    ))}
                  </div>
                  {errors.paymentMethods && (
                    <p className="text-sm text-destructive">
                      {errors.paymentMethods}
                    </p>
                  )}

                  {formData.paymentMethods.includes("GoCardless") && (
                    <div className="animate-in fade-in slide-in-from-top-2 pt-2">
                      <Label
                        htmlFor="gocardless"
                        className="text-secondary mb-1.5 block"
                      >
                        GoCardless Signup Link
                      </Label>
                      <Input
                        id="gocardless"
                        type="url"
                        placeholder="https://pay.gocardless.com/..."
                        value={formData.goCardlessLink}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            goCardlessLink: e.target.value,
                          })
                        }
                        className={cn(
                          errors.goCardlessLink && "border-destructive",
                        )}
                      />
                      {errors.goCardlessLink && (
                        <p className="text-sm text-destructive mt-1">
                          {errors.goCardlessLink}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Section 5: Reviews */}
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-secondary flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-secondary text-white flex items-center justify-center text-sm">
                    5
                  </span>
                  Reviews
                </h2>

                <div>
                  <Label
                    htmlFor="reviewLink"
                    className="text-secondary mb-1.5 block"
                  >
                    Review Generation Link
                  </Label>
                  <Input
                    id="reviewLink"
                    type="url"
                    placeholder="Your Google Business Profile or review page URL"
                    value={formData.reviewLink}
                    onChange={(e) =>
                      setFormData({ ...formData, reviewLink: e.target.value })
                    }
                    className={cn(errors.reviewLink && "border-destructive")}
                  />
                  <p className="text-sm text-secondary/60 mt-1.5">
                    Usually your Google Business Profile link
                  </p>
                  {errors.reviewLink && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.reviewLink}
                    </p>
                  )}
                </div>
              </div>

              {/* Section 6: Your Round */}
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-secondary flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-secondary text-white flex items-center justify-center text-sm">
                    6
                  </span>
                  Your Round
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label
                      htmlFor="minPrice"
                      className="text-secondary mb-1.5 block"
                    >
                      Minimum Window Cleaning Price
                    </Label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-secondary font-medium">
                        £
                      </span>
                      <Input
                        id="minPrice"
                        placeholder="10.00"
                        value={formData.minPrice}
                        onChange={(e) =>
                          setFormData({ ...formData, minPrice: e.target.value })
                        }
                        className={cn(
                          "pl-8",
                          errors.minPrice && "border-destructive",
                        )}
                      />
                    </div>
                    {errors.minPrice && (
                      <p className="text-sm text-destructive mt-1">
                        {errors.minPrice}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label
                      htmlFor="yearlyValue"
                      className="text-secondary mb-1.5 block"
                    >
                      Current Yearly Round Value
                    </Label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-secondary font-medium">
                        £
                      </span>
                      <Input
                        id="yearlyValue"
                        placeholder="25,000"
                        value={formData.yearlyValue}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            yearlyValue: e.target.value,
                          })
                        }
                        className={cn(
                          "pl-8",
                          errors.yearlyValue && "border-destructive",
                        )}
                      />
                    </div>
                    {errors.yearlyValue && (
                      <p className="text-sm text-destructive mt-1">
                        {errors.yearlyValue}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Section 7: Go Live Date */}
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-secondary flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-secondary text-white flex items-center justify-center text-sm">
                    7
                  </span>
                  Go Live Date
                </h2>

                <div className="space-y-4">
                  <p className="text-sm text-secondary/70 mb-2">
                    When would you like to aim to go live? Please note that it
                    can sometimes take a couple of weeks to get everything set
                    up, particularly when we are busy.
                  </p>

                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { id: "ASAP", label: "ASAP" },
                      { id: "specific", label: "Choose a Date" },
                    ].map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => {
                          setFormData({
                            ...formData,
                            goLivePreference: option.id,
                            goLiveDate:
                              option.id === "ASAP" ? "" : formData.goLiveDate,
                          });
                          if (errors.goLivePreference) {
                            const newErrors = { ...errors };
                            delete newErrors.goLivePreference;
                            setErrors(newErrors);
                          }
                        }}
                        className={cn(
                          "p-4 rounded-xl border text-center font-medium transition-all",
                          formData.goLivePreference === option.id
                            ? "bg-secondary border-secondary text-white shadow-md"
                            : "bg-white border-slate-200 text-secondary hover:border-secondary/50",
                          errors.goLivePreference && "border-destructive",
                        )}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  {errors.goLivePreference && (
                    <p className="text-sm text-destructive">
                      {errors.goLivePreference}
                    </p>
                  )}

                  {formData.goLivePreference === "specific" && (
                    <div className="animate-in fade-in slide-in-from-top-2 pt-2">
                      <Label
                        htmlFor="goLiveDate"
                        className="text-secondary mb-1.5 block"
                      >
                        Preferred Go Live Date
                      </Label>
                      <Input
                        id="goLiveDate"
                        type="date"
                        min={new Date().toISOString().split("T")[0]}
                        value={formData.goLiveDate}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            goLiveDate: e.target.value,
                          })
                        }
                        className={cn(
                          errors.goLiveDate && "border-destructive",
                        )}
                      />
                      {errors.goLiveDate && (
                        <p className="text-sm text-destructive mt-1">
                          {errors.goLiveDate}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="pt-4">
                <Button
                  size="lg"
                  className="w-full h-14 text-lg bg-primary hover:bg-[#e04825] text-white shadow-lg"
                  onClick={() => {
                    if (validateStep1()) {
                      setStep(2);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }
                  }}
                >
                  Continue to Target Areas
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="p-6 md:p-10 space-y-10">
              <div className="p-4 bg-secondary/5 border border-secondary/20 rounded-xl flex gap-3">
                <Info className="w-6 h-6 text-secondary shrink-0" />
                <div>
                  <h4 className="font-medium text-secondary text-sm mb-1">
                    How Town Targeting Works
                  </h4>
                  <p className="text-sm text-secondary/70">
                    List each town you want to target along with its phone area
                    code. Please note that larger cities may be broken down into
                    separate areas depending on their size — for example, London
                    is divided into multiple zones. Each area counts as one town
                    for pricing purposes.
                  </p>
                </div>
              </div>

              {/* Section 1: Your Target Towns */}
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-secondary flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-secondary text-white flex items-center justify-center text-sm">
                    1
                  </span>
                  Your Target Towns
                </h2>

                <div className="space-y-4">
                  <div className="grid grid-cols-[1fr_140px_48px] gap-4 px-2 hidden md:grid">
                    <span className="text-sm font-medium text-secondary">
                      Town Name
                    </span>
                    <span className="text-sm font-medium text-secondary">
                      Area Code
                    </span>
                    <span></span>
                  </div>

                  {formData.towns.map((town, index) => (
                    <div key={index} className="space-y-2">
                      <div className="grid grid-cols-1 md:grid-cols-[1fr_140px_48px] gap-4 items-start">
                        <div>
                          <Label className="md:hidden text-xs mb-1 block text-slate-500">
                            Town Name
                          </Label>
                          <Input
                            placeholder="e.g. Bath"
                            value={town.name}
                            onChange={(e) =>
                              updateTown(index, "name", e.target.value)
                            }
                            className={cn(
                              checkTownWarning(town.name, town.code) &&
                                "border-amber-500 focus-visible:ring-amber-500",
                              errors[`town_${index}_name`] &&
                                "border-destructive",
                            )}
                          />
                          {errors[`town_${index}_name`] && (
                            <p className="text-xs text-destructive mt-1">
                              {errors[`town_${index}_name`]}
                            </p>
                          )}
                        </div>
                        <div>
                          <Label className="md:hidden text-xs mb-1 block text-slate-500">
                            Area Code
                          </Label>
                          <Input
                            placeholder="01225"
                            value={town.code}
                            onChange={(e) =>
                              updateTown(index, "code", e.target.value)
                            }
                            className={cn(
                              checkTownWarning(town.name, town.code) &&
                                "border-amber-500 focus-visible:ring-amber-500",
                              errors[`town_${index}_code`] &&
                                "border-destructive",
                            )}
                          />
                          {errors[`town_${index}_code`] && (
                            <p className="text-xs text-destructive mt-1">
                              {errors[`town_${index}_code`]}
                            </p>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="w-full md:w-12 h-12 text-destructive border-destructive/20 hover:bg-destructive/10 hover:text-destructive mt-2 md:mt-0"
                          disabled={formData.towns.length === 1}
                          onClick={() => removeTown(index)}
                        >
                          <Trash2 className="w-5 h-5" />
                          <span className="md:hidden ml-2">Remove</span>
                        </Button>
                      </div>
                      {checkTownWarning(town.name, town.code) && (
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2 text-amber-800 text-sm animate-in fade-in">
                          <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />
                          <p>
                            We may already cover this area. Please double check
                            with us before proceeding.
                          </p>
                        </div>
                      )}
                    </div>
                  ))}

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-12 border-dashed border-2 border-secondary/30 bg-secondary/5 text-secondary hover:bg-secondary/10 hover:border-secondary/50"
                    onClick={addTown}
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Add Another Town
                  </Button>
                </div>
              </div>

              {/* Section 2: Pricing Summary */}
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-secondary flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-secondary text-white flex items-center justify-center text-sm">
                    2
                  </span>
                  Pricing Summary
                </h2>

                <div className="bg-slate-50 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-6">
                    <Label
                      htmlFor="vat-toggle"
                      className="text-base font-medium text-secondary cursor-pointer"
                    >
                      Include VAT (20%)
                    </Label>
                    <Switch
                      id="vat-toggle"
                      checked={formData.includeVat}
                      onCheckedChange={(c) =>
                        setFormData({ ...formData, includeVat: c })
                      }
                    />
                  </div>

                  <div className="p-4 bg-secondary/5 rounded-lg mb-6 text-sm text-secondary/80">
                    <strong className="text-secondary">Pricing:</strong>{" "}
                    £749/month includes up to 7 towns. Additional towns are
                    £70/month each.
                  </div>

                  <div className="space-y-4 mb-6">
                    <div className="bg-white p-4 rounded-lg border border-slate-200">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-secondary font-medium">
                          Monthly Fee
                        </span>
                        <span className="font-bold text-secondary">
                          £{monthlyFee.toFixed(2)}
                        </span>
                      </div>
                      <div className="text-sm text-slate-500 mb-3">
                        {validTownsCount <= INCLUDED_TOWNS
                          ? `${validTownsCount} town${validTownsCount !== 1 ? "s" : ""} (up to ${INCLUDED_TOWNS} included)`
                          : `${INCLUDED_TOWNS} towns included + ${extraTowns} additional @ £${ADDITIONAL_TOWN_PRICE} each`}
                      </div>
                      {formData.includeVat && (
                        <div className="flex justify-between items-center pt-3 border-t border-dashed border-slate-200 text-sm text-slate-500">
                          <span>VAT (20%)</span>
                          <span>£{monthlyVat.toFixed(2)}</span>
                        </div>
                      )}
                    </div>

                    <div className="bg-white p-4 rounded-lg border border-slate-200">
                      <div className="flex justify-between items-center">
                        <span className="text-secondary font-medium">
                          One-Time Setup Fee
                        </span>
                        <span className="font-bold text-secondary">
                          £{SETUP_FEE.toFixed(2)}
                        </span>
                      </div>
                      {formData.includeVat && (
                        <div className="flex justify-between items-center pt-3 mt-3 border-t border-dashed border-slate-200 text-sm text-slate-500">
                          <span>VAT (20%)</span>
                          <span>£{setupVat.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-secondary text-white rounded-xl p-6 shadow-lg">
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-white/90 font-medium">
                        Monthly Total{" "}
                        <span className="text-sm font-normal opacity-80">
                          {formData.includeVat ? "(inc. VAT)" : "(ex. VAT)"}
                        </span>
                      </span>
                      <span className="text-2xl font-bold">
                        £{monthlyTotal.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-4 border-t border-white/20">
                      <span className="text-white/90 font-medium">
                        Setup Fee{" "}
                        <span className="text-sm font-normal opacity-80">
                          {formData.includeVat ? "(inc. VAT)" : "(ex. VAT)"}
                        </span>
                      </span>
                      <span className="text-lg font-semibold">
                        £{setupTotal.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button
                  variant="outline"
                  size="lg"
                  className="h-14 text-lg border-secondary/20 text-secondary hover:bg-secondary/5 flex-1 sm:flex-none sm:w-1/3"
                  onClick={() => {
                    setStep(1);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  disabled={isSubmitting}
                >
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  Back
                </Button>
                <Button
                  size="lg"
                  className="h-14 text-lg bg-primary hover:bg-[#e04825] text-white shadow-lg flex-1"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    "Complete Onboarding"
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>

        <p className="text-center text-sm text-slate-500 mt-8">
          Your information is secure and will only be used for onboarding
          purposes.
        </p>
      </main>
      <Footer />
    </div>
  );
};

export default Signup;
